import {
  Auth,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  getAuth,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  UserCredential
} from "@firebase/auth";
import React, {ReactElement, useState} from "react";
import {Box, Button, ButtonBase, Card, IconButton, InputAdornment, TextField, Tooltip, useTheme} from "@mui/material";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import {PD_MD, PD_XLG2} from "./dimens";
import {colorRed} from "./colors";
import {get, set} from "@firebase/database";
import {Render, Renderable, UserCache} from "./types";
import {BaseApp, getProvisioningId, renderStamp, setLoginCredentials, setProvisioningId} from "./BaseApp";
import {HtmlFragment} from "./HtmlFragment";
import {ExitToAppOutlined, RefreshOutlined, Visibility, VisibilityOff} from "@mui/icons-material";
import {User} from "./entities";
import {validateEmail} from "./text_util";
import {PROVISIONING_CODE_MAX_LENGTH, PROVISIONING_CODE_MIN_LENGTH} from "./constants";
import {md5} from "./md5";
import {dbRef, ProvisioningContext, SystemProvisioningIds} from "./database";
import {JSON_OBJECT} from "./json/helpers";
import {StyledBoxColumn, StyledBoxRow} from "shared/StyledComponents";
import {FirebaseOptions} from "firebase/app";
import {MailApi, MailRequest} from './MailApi';

enum View {
  VERIFY_CUSTOMER,
  SETUP,
  LOGIN,
  SIGNUP,
}

export async function loginWithEmailAndPassword(auth: Auth, email: string, password: string, setError?: (error: string) => void) {
  if (!validateEmail(email)) {
    setError("Invalid email: " + email + ". Please enter a valid email.");
    return;
  }
  if (password.length < 8) {
    setError("Please enter a password with at least 8 characters.");
    return;
  }
  await signInWithEmailAndPassword(auth, email, password)
    .then((value: UserCredential) => {
      setLoginCredentials({
        email: email,
        password: password,
      });
    }).catch(reason => {
      setError?.("Error logging in. Please check your email and password, and try again.");
      throw new Error();
    });
}

export async function signupWithEmailAndPassword(auth: Auth, firstname: string, lastname: string, email: string, password: string, setError?: (error: string) => void) {
  if (!validateEmail(email)) {
    setError("Invalid email: " + email + ". Please enter a valid email.");
    throw new Error();
  }
  if (password.length < 8) {
    setError("Please enter a password with at least 8 characters.");
    throw new Error();
  }
  await createUserWithEmailAndPassword(auth, email, password)
    .then((value: UserCredential) => {
      const user = new User(
        value.user.uid,
        firstname,
        lastname,
        null,
        email,
        Date.now(),
        null);
      const object = JSON_OBJECT.serializeObject(user);
      return set(dbRef("users/" + value.user.uid), object).then(() => user);
    })
    .then(user => UserCache.getInstance().setUser(user.uid, user))
    .then(() => {
      setLoginCredentials({
        email: email,
        password: password,
      });
    })
    .catch(reason => {
      setError?.("Error signing up. Please try again later.");
      throw new Error();
    });
}

function SignupFragment(props: { auth: Auth, setView: (view: View) => void, redirectPath?: string, }) {
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [signingUp, setSigningUp] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);

  function doSignup() {
    setSigningUp(true);
    signupWithEmailAndPassword(props.auth, firstname, lastname, email, password, setError)
      .then(() => window.location.href = props.redirectPath || "/")
      .catch(() => {/* do nothing */
      }).finally(() => setSigningUp(false));
  }

  const onKeyDown = event => {
    if (event.keyCode === 13) {
      event.preventDefault();
      doSignup();
    }
  };

  const signupEnabled = email.length > 0 && firstname.length > 0 && password.length > 0 && agree;
  return <Box style={{display: "flex", flexDirection: "column", gap: PD_MD}}>
    <Box style={{display: "flex", gap: PD_MD}}>
      <TextField autoFocus size="small" style={{flexGrow: 1}} placeholder="First name" required onKeyDown={onKeyDown}
                 onChange={event => setFirstname(event.target.value?.trim() || "")}/>
      <TextField size="small" style={{flexGrow: 1}} placeholder="Last name" onKeyDown={onKeyDown}
                 onChange={event => setLastname(event.target.value?.trim() || "")}/>
    </Box>
    <TextField size="small" placeholder="Email" type="email" required onKeyDown={onKeyDown}
               onChange={event => setEmail(event.target.value?.trim() || "")}/>
    <TextField
      size="small"
      placeholder="Password"
      type={showPassword ? "text" : "password"}
      required
      onKeyDown={onKeyDown}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={() => setShowPassword(showPassword => !showPassword)}
              onMouseDown={(event) => event.preventDefault()}
              edge="end"
            >
              {showPassword ? <VisibilityOff/> : <Visibility/>}
            </IconButton>
          </InputAdornment>
        )
      }}
      onChange={event => setPassword(event.target.value?.trim() || "")}
    />
    {error.length > 0 ? <Typography variant="caption" style={{color: colorRed,}}>{error}</Typography> : null}
    <Box style={{display: "flex", gap: 12, alignItems: "center"}}>
      <Checkbox checked={agree} onChange={(event, checked) => setAgree(checked)}/>
      <Typography variant={"body2"} style={{marginLeft: -12}}>I have read and agree to the
        <a href="#"
           onClick={() => BaseApp.CONTEXT.showDialog(null, () => <HtmlFragment url="/privacy.html"/>)}><u> Privacy
          Policy</u>
        </a> and
        <a href="#"
           onClick={() => BaseApp.CONTEXT.showDialog(null, () => <HtmlFragment url="/terms.html"/>)}><u> Terms &
          Conditions</u>
        </a>.</Typography>
    </Box>
    <Button
      variant="contained"
      disabled={!signupEnabled || signingUp}
      onClick={() => doSignup()}>Sign Up
    </Button>
    <hr/>
    <Typography variant="body2" style={{marginTop: PD_MD}}>
      Already have an account?&nbsp;
      <a href="#" onClick={() => props.setView(View.LOGIN)}><u>Log in</u></a></Typography>
  </Box>;
}

function renderContinueWithGoogleButton(auth: Auth, onClick: () => void) {
  return <button className="gsi-material-button" onClick={() => onClick()}>
    <div className="gsi-material-button-state"></div>
    <div className="gsi-material-button-content-wrapper">
      <div className="gsi-material-button-icon">
        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style={{display: "block"}}>
          <path fill="#EA4335"
                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
          <path fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
          <path fill="#FBBC05"
                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
          <path fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
          <path fill="none" d="M0 0h48v48H0z"></path>
        </svg>
      </div>
      <span className="gsi-material-button-contents">Continue with Google</span>
      <span style={{display: "none"}}>Continue with Google</span>
    </div>
  </button>;
}

enum ResetPasswordStatus {
  NONE,
  SENDING,
  SENT,
}

function LoginFragment(props: {
  auth: Auth,
  setView?: (view: View) => void,
  providers: LoginProviders[],
  signupDisabled?: boolean,
  redirectPath?: string,
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loggingIn, setLoggingIn] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetPasswordMessage, setResetPasswordMessage] = useState("");
  const [resetPasswordStatus, setResetPasswordStatus] = useState<ResetPasswordStatus>(ResetPasswordStatus.NONE);

  const providers = props.providers || [LoginProviders.EMAIL];

  const resetPassword = (email: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      // Prepare the email data
      const emailData: MailRequest = {
        fromId: "noreply_fynedental_com",
        to: forgotPasswordEmail,
        subject: "Password Reset Request",
        contentHtml: {
          method: "POST",
          url: "https://fyneapps-apps-cms-service.web.app/v1/contents/aa782037693554def1804c7d6508494f",
          body: {
            renderArgs: {
              liquid: {
                firstName: "Fynedental User",
                resetPasswordLink: "https://fynedental.com/resetPassword"
              }
            }
          },
          content: "$.body"
        }
      };

      const mailApi = MailApi.getInstance();
      mailApi.sendEmail(emailData)
        .then(() => {
          resolve("A password reset link has been sent to your email.");
        })
        .catch(error => {
          setError(error.message)
          reject("Error processing request.");
        });
    });
  };

  const handleForgotPassword = () => {
    setIsForgotPassword(true);
  };

  function handleResetPassword() {
    setResetPasswordStatus(ResetPasswordStatus.SENDING);
    if (!validateEmail(forgotPasswordEmail)) {
      setError("Invalid email: " + forgotPasswordEmail + ". Please enter a valid email.");
      return;
    }

    resetPassword(forgotPasswordEmail)
      .then((message: string) => {
        setResetPasswordMessage(message);
        setResetPasswordStatus(ResetPasswordStatus.SENT); // Indicate that the reset email has been sent
        setForgotPasswordEmail("");
      })
      .catch((error: string) => {
        setError(error);
      });
  }


  const doLogin = (redirectPath: string) => {
    if (!validateEmail(email)) {
      setError("Invalid email: " + email + ". Please enter a valid email.");
      return;
    }
    if (password.length < 8) {
      setError("Please enter a password with at least 8 characters.");
      return;
    }
    setLoggingIn(true);
    loginWithEmailAndPassword(props.auth, email, password)
      .then(() => {
        setLoginCredentials({
          email: email,
          password: password,
        });
        window.location.href = redirectPath || "/";
      })
      .catch(reason => {
        setError("Error logging in. Please check your email and password, and try again.");
      }).finally(() => {
      setLoggingIn(false);
    })
  };

  function doLoginWithGoogle() {
    signInWithPopup(props.auth, new GoogleAuthProvider())
      .then((result) => {
        // This gives you a Google Access Token. You can use it to access the Google API.
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const token = credential.accessToken;
        // The signed-in user info.
        const user = result.user;
        const info = getAdditionalUserInfo(result);
        // IdP data available using getAdditionalUserInfo(result)
        // ...
      }).catch((error) => {
      // Handle Errors here.
      const errorCode = error.code;
      const errorMessage = error.message;
      // The email of the user's account used.
      const email = error.customData.email;
      // The AuthCredential type that was used.
      const credential = GoogleAuthProvider.credentialFromError(error);
      setError("Error logging in. Please check your browser settings, and try again.");
    });
  }

  const onKeyDown = event => {
    if (event.keyCode === 13) {
      event.preventDefault();
      doLogin(props.redirectPath);
    }
  };

  const onSignInAgain = () => {
    setIsForgotPassword(false);
    setResetPasswordStatus(ResetPasswordStatus.NONE);
    setResetPasswordMessage("");
    setError("");
  }

  const loginEnabled = email.length > 0 && password.length > 0;

  return <StyledBoxColumn>
    {!isForgotPassword && providers.includes(LoginProviders.GOOGLE) && !isForgotPassword && (
      <>
        {renderContinueWithGoogleButton(props.auth, doLoginWithGoogle)}
        {providers.length > 1 && <Typography style={{textAlign: "center"}}>OR</Typography>}
      </>
    )}
    {!isForgotPassword && providers.includes(LoginProviders.EMAIL) && (
      <>
        <TextField
          size="small"
          autoFocus
          placeholder="Email"
          type="email"
          required
          onKeyDown={onKeyDown}
          onChange={event => setEmail(event.target.value?.trim() || "")}
        />
        <TextField
          size="small"
          placeholder="Password"
          type={showPassword ? "text" : "password"}
          required
          onKeyDown={onKeyDown}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(showPassword => !showPassword)}
                  onMouseDown={event => event.preventDefault()}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff/> : <Visibility/>}
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={event => setPassword(event.target.value?.trim() || "")}
        />

        <Button
          variant="contained"
          disabled={!loginEnabled || loggingIn}
          onClick={() => doLogin(props.redirectPath)}
        >
          Log in
        </Button>
        <Typography variant="body2">
          <a href="#" onClick={() => handleForgotPassword()}><u>Forgot password?</u></a>
        </Typography>
      </>
    )}

    {/* Forgot Password Form */}
    {isForgotPassword && resetPasswordStatus !== ResetPasswordStatus.SENT
      ? <StyledBoxColumn>
        <Typography>Enter your email, and we'll send a link to reset your password.</Typography>
        <TextField
          size="small"
          placeholder="Email"
          value={forgotPasswordEmail}
          onChange={e => setForgotPasswordEmail(e.target.value)}
          fullWidth
          required
          style={{marginBottom: '12px'}}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleResetPassword()}
          disabled={!forgotPasswordEmail.length || resetPasswordStatus === ResetPasswordStatus.SENDING}
        >
          Submit
        </Button>
        <Typography style={{marginTop: '20px', textAlign: 'center'}}>
          Remember your password?{' '}
          <a href="#" onClick={() => onSignInAgain()}>Go back to Sign In</a>
        </Typography>

        {/* Display reset password message below button */}
        {resetPasswordMessage && (
          <Typography variant="caption"
                      style={{color: resetPasswordMessage.includes('sent') ? 'green' : 'red'}}>
            {resetPasswordMessage}
          </Typography>
        )}
      </StyledBoxColumn>
      : null}

    {error.length > 0 && <Typography variant="caption" style={{color: colorRed}}>{error}</Typography>}

    {/* If reset link is sent */}
    {resetPasswordStatus === ResetPasswordStatus.SENT
      ? <StyledBoxColumn>
        <Typography variant="h6" color="primary">
          We have sent a reset link to your email.
        </Typography>
        <Button
          variant="contained"
          onClick={() => onSignInAgain()}  // Back to Sign In
        >
          Go back to Sign In
        </Button>
      </StyledBoxColumn>
      : null}

    <hr/>
    <Typography variant={"body2"}>
      By using this app, you agree to the <a href="#" onClick={() => {
      BaseApp.CONTEXT.showDialog(null, () => <HtmlFragment url="/privacy.html"/>);
    }}><u>Privacy Policy</u></a> and <a href="#" onClick={() => BaseApp.CONTEXT.showDialog(null, () => <HtmlFragment
      url="/terms.html"/>)}>
      <u>Terms & Conditions</u></a>.
    </Typography>

    {/* Sign Up Link */}
    {providers.includes(LoginProviders.EMAIL) && !props.signupDisabled && !isForgotPassword && (
      <Typography variant="body2" style={{marginTop: PD_MD}}>
        Don't have an account?&nbsp;<a href="#" onClick={() => props.setView(View.SIGNUP)}><u>Sign up</u></a>
      </Typography>
    )}
  </StyledBoxColumn>

}


function VerifyCustomerFragment(props: {
  auth: Auth,
  setView?: (view: View) => void,
  setupRequired?: boolean | ((provisioningId: string) => Promise<boolean>),
}) {
  const [provisioningCode, setProvisioningCode] = useState("");
  const [error, setError] = useState("");

  async function setupIfNeeded(provisioningId: string): Promise<boolean> {
    if (!props.setupRequired || ((typeof props.setupRequired === "function") && !(await props.setupRequired(provisioningId)))) {
      return true;
    }
    // TODO: Do This
    // if (await props.onSetup?.(provisioningId)) {
    //   await set(dbRef("/provisionings/" + provisioningId + "/setupComplete"), true);
    //   return true;
    // }
    return false;
  }

  async function doVerifyCustomer() {
    const provisioningId = md5(provisioningCode);
    const val = await get(dbRef("/provisionings/" + provisioningId));
    if (val.exists()) {
      setProvisioningId(provisioningId);
      setupIfNeeded(provisioningId).then(provisioningOk => {
        if (provisioningOk) {
          window.location.href = "/";
        } else {
          props.setView(View.SETUP);
        }
      });
    } else {
      setError("Provisioning code is not valid.");
    }
  }

  const verifyCustomerEnabled = provisioningCode.length >= PROVISIONING_CODE_MIN_LENGTH;
  const onKeyDown = event => {
    if (verifyCustomerEnabled && event.keyCode === 13) {
      event.preventDefault();
      doVerifyCustomer();
    }
  };

  return <Box style={{display: "flex", flexDirection: "column", gap: PD_MD}}>
    <Typography>Enter your provisioning code to continue to the app.</Typography>
    <TextField
      size="small"
      autoFocus
      placeholder="Provisioning code" type="text" required onKeyDown={onKeyDown}
      inputProps={{maxLength: PROVISIONING_CODE_MAX_LENGTH}}
      value={provisioningCode}
      onChange={event => setProvisioningCode(event.target.value?.trim().toUpperCase() || "")}/>
    {error.length > 0 ? <Typography variant="caption" style={{color: colorRed,}}>{error}</Typography> : null}
    <Button
      variant="contained"
      disabled={!verifyCustomerEnabled}
      onClick={() => doVerifyCustomer()}>Continue</Button>
  </Box>;
}

function SetupErrorFragment() {
  return <Box style={{display: "flex", flexDirection: "column", gap: PD_MD}}>
    <Typography>There was an error with this setup. Please contact support.</Typography>
  </Box>
}

export type FirebaseConfig = {
  noOverride?: boolean, // When this is true, if this is a plugin app, host doesn't override default firebase config.
  options: FirebaseOptions,
}

export type LoginProvisioningConfig = {
  provisioningId?: string,
  enabled?: boolean,
  membersUseSharedProvisioningContext?: boolean, // If true, members from different provisioning contexts all use the same (MAIN) provisioning context. This allows users to interact with apps in different provisioning contexts.
  usernameEnabled?: boolean,
  signupDisabled?: boolean,
  onChangeProvisioning?: () => void,
  setupRequired?: boolean | ((provisioningId: string) => Promise<boolean>), // See fyneapps/admin to see an example.
  renderSetup?: (provisioningId: string, onSetupComplete: () => void) => ReactElement, // True means setup handled. If setupRequired "true" and onSetup not set, shows an error.
  renderOptions?: {
    fullscreen?: boolean,
  };
}

export type LoginRolesConfig = {
  enabled?: boolean,
  onSwitchLoginRole: () => void,
}

export type LoginDomainsConfig = {
  enabled?: boolean,
  domainName?: string,
}

export enum LoginProviders {
  EMAIL = "email",
  GOOGLE = "google",
  // APPLE = "apple",
}

export type LoginUiProps = {
  heroElement?: Renderable,
  topDecorator?: ReactElement,
  bottomDecorator?: ReactElement,
}

export type LoginConfig = {
  noLogin?: boolean, // If true, doesn't show login on app startup (if not logged-in).
  optionalLogin?: boolean, // If true, allows login but is not shown automatically.
  guestLogin?: boolean, // If true, allows guest member auth to be created and persisted in session storage.
  provisioning?: LoginProvisioningConfig,
  roles?: LoginRolesConfig,
  domains?: LoginDomainsConfig,
  providers?: LoginProviders[],
  uiProps?: LoginUiProps,
}

export type LoginProps = {
  config?: LoginConfig,
  redirectPath?: string,
}

function LoginContainer(props: LoginProps & { view: View, children }): ReactElement {
  const theme = useTheme();
  const large = true;//useMediaQuery(theme.breakpoints.up('lg'));
  const loginForm = <Card style={{
    padding: 24,
    display: "flex",
    flexDirection: "column",
    width: "92dvh",
    maxWidth: 400,
    gap: 24,
  }}>
    {renderStamp()}
    {props.children}
  </Card>;
  const topDecorator = props.config?.uiProps?.topDecorator;
  const bottomDecorator = props.config?.uiProps?.bottomDecorator;
  const heroElement = props.config?.uiProps?.heroElement;
  return <Box style={{
    width: "100%",
    paddingTop: PD_XLG2,
    paddingBottom: PD_XLG2,
    height: large ? "100%" : null,
    display: "flex",
    flexDirection: "column",
    alignItems: large ? "center" : null,
    justifyContent: large ? "center" : null,
    background: theme.palette.background.default,
  }}>
    {Boolean(heroElement)
      ? <img src={"/assets/wallpapers/4.jpg"}
             style={{position: "absolute", width: "100%", height: "100%", objectFit: "cover", opacity: .75}}/>
      : null}
    {props.config?.roles?.enabled
      ? <ButtonBase
        style={{width: 24, height: 24, position: "absolute", top: 24, right: 24}}
        onClick={() => {
          props.config.roles.onSwitchLoginRole();
          window.location.href = "/";
        }}>
        <RefreshOutlined/>
      </ButtonBase>
      : (props.view !== View.VERIFY_CUSTOMER && props.config?.provisioning?.enabled
          ? <Tooltip title={"Change Provisioning Code"}>
            <IconButton
              style={{position: "absolute", top: 24, right: 24}}
              onClick={() => {
                props.config.provisioning.onChangeProvisioning?.();
                window.location.href = "/";
              }}>
              <ExitToAppOutlined/>
            </IconButton>
          </Tooltip>
          : null
      )}
    <StyledBoxColumn>
      {topDecorator}
      {heroElement
        ? (large
          ? <StyledBoxRow style={{gap: 0}}>
            {Render(heroElement, null)}
            {loginForm}
          </StyledBoxRow>
          : <StyledBoxColumn>
            <div style={{alignSelf: "center"}}>
              {loginForm}
            </div>
            {Render(heroElement, null)}
          </StyledBoxColumn>)
        : loginForm}
      {bottomDecorator}
    </StyledBoxColumn>
  </Box>;
}

export function Login(props: LoginProps) {
  const auth = getAuth();
  const provisioningId = getProvisioningId();
  const [view, setView] = useState((!provisioningId || provisioningId === SystemProvisioningIds.MAIN) && props.config?.provisioning?.enabled
    ? View.VERIFY_CUSTOMER :
    (typeof props.config?.provisioning?.setupRequired === "boolean" && Boolean(props.config?.provisioning?.setupRequired)
      ? View.SETUP
      : View.LOGIN));

  function getFragment(providers: LoginProviders[], signupDisabled: boolean, view: View, setView: (view: View) => void, auth: Auth) {
    switch (view) {
      case View.VERIFY_CUSTOMER:
        return <VerifyCustomerFragment
          auth={auth} setView={setView}
          setupRequired={props.config?.provisioning?.setupRequired}
        />
      case View.SETUP:
        const provisioningId = getProvisioningId();
        const setupRendered = props.config?.provisioning?.renderSetup?.(provisioningId, () => {
          new ProvisioningContext(provisioningId).dbRef_setVal("/provisionings/" + provisioningId + "/setupComplete", true)
            .then(() => setView(View.LOGIN));
        });
        return setupRendered || <SetupErrorFragment/>;
      case View.LOGIN:
        return <LoginFragment auth={auth} setView={setView} providers={providers} signupDisabled={signupDisabled}
                              redirectPath={props.redirectPath}/>
      case View.SIGNUP:
        return <SignupFragment auth={auth} setView={setView} redirectPath={props.redirectPath}/>
    }
  }

  const fragment = getFragment(props.config?.providers, props.config?.provisioning?.signupDisabled, view, setView, auth);
  return !props.config?.provisioning?.renderOptions?.fullscreen
    ? <LoginContainer view={view} {...props}>{fragment}</LoginContainer>
    : fragment;
}
